'use client'

import { toast } from 'sonner'

import { Message } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { useActiveOrg, useActiveOrgUpdates } from '~/hooks/org'
import {
  passkeyConfirmationHeaders,
  usePasskeyConfirmation,
} from '~/hooks/usePasskeyConfirmation'
import { useServerRequests } from '~/hooks/useRequests'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import {
  OrgMember,
  OrgMemberRole,
  PasskeyConfirmationAction,
  UserKind,
} from '~/types'
import { plural } from '~/utils/display'
import { getMemberEventProperties, getMemberLabel } from '~/utils/orgMembers'

type SignerKeyRemoval = {
  smartAccountId: string
  name: string
  outcome: 'proposed' | 'needs_recovery' | 'failed'
}

// Null when the agent's keys could not be checked; absent from a backend that does not report them.
type RemoveMemberResponse = { signerKeyRemovals?: SignerKeyRemoval[] | null }

const announceSignerKeyRemovals = (removals: SignerKeyRemoval[]) => {
  const withOutcome = (outcome: SignerKeyRemoval['outcome']) =>
    removals.filter((removal) => removal.outcome === outcome)
  const namesOf = (accounts: SignerKeyRemoval[]) =>
    accounts.map((account) => account.name).join(', ')
  const proposed = withOutcome('proposed').length
  const needsRecovery = withOutcome('needs_recovery')
  const failed = withOutcome('failed')
  if (proposed > 0) {
    toast.info(
      `Sign ${proposed} ${plural('proposal', proposed)} to remove the agent key`,
    )
  }
  if (needsRecovery.length > 0) {
    toast.warning(
      `Reset signers on ${namesOf(needsRecovery)} from the recovery account to remove the agent key`,
    )
  }
  if (failed.length > 0) {
    toast.error(
      `Could not propose removing the agent key on ${namesOf(failed)}. Edit those signers from the account settings.`,
    )
  }
}

export const RemoveMemberDialogContent = ({
  member,
  closeDialog,
  onRemove,
}: {
  member: OrgMember
  closeDialog: () => void
  onRemove?: () => void
}) => {
  const { org } = useActiveOrg()
  const { removeOrgMember } = useActiveOrgUpdates()
  const { smartAccounts } = useSmartAccounts()
  const { serverPostRequest, isProcessing } = useServerRequests()
  const { confirmWithPasskey, isConfirming } = usePasskeyConfirmation()

  const isAgent = member.kind === UserKind.Agent
  const signerOnSmartAccounts = smartAccounts.filter(
    (account) =>
      account.signers.some(
        (signer) => signer.user && signer.user.id === member.id,
      ) || account.eoaSigners.some((signer) => signer.userId === member.id),
  )
  const isSigner = signerOnSmartAccounts.length > 0

  const removeMember = async () => {
    const role = member.role === OrgMemberRole.Owner ? 'Owner' : 'Member'
    const confirmationId = await confirmWithPasskey({
      action: PasskeyConfirmationAction.MemberRemove,
      orgId: org?.id,
      resourceId: member.id,
    })
    if (!confirmationId) return

    const result = await serverPostRequest<RemoveMemberResponse>({
      path: '/api/org/user/remove',
      params: {
        orgId: org?.id,
        memberId: member.id,
      },
      headers: passkeyConfirmationHeaders(confirmationId),
      successMessage: `${role} removed`,
      errorMessage: 'Failed to remove member',
    })
    if (result) {
      const removals = result.data?.signerKeyRemovals
      if (removals === null) {
        toast.error(
          'Could not check the agent key. Remove it from the account settings.',
        )
      } else {
        announceSignerKeyRemovals(removals ?? [])
      }
      removeOrgMember(member.id)
      closeDialog()
      onRemove?.()
    }
  }

  return (
    <SplitsDialogContent
      id="removeMember"
      title={`Remove ${getMemberLabel(member)} from workspace?`}
      variant="destructive"
      isLoading={isProcessing || isConfirming}
      deleteButton={{
        text: 'Remove',
        onClick: removeMember,
        eventProperties: getMemberEventProperties(member),
      }}
    >
      <div className="space-y-4">
        <p>
          They will no longer be able to access the <span>{org?.name}</span>{' '}
          workspace
        </p>
        {isSigner && isAgent && (
          <Message variant="warning">
            <p>
              Its key will stay a signer on{' '}
              {signerOnSmartAccounts.length.toString()}{' '}
              {plural('account', signerOnSmartAccounts.length)} until it is
              removed from them.
            </p>
          </Message>
        )}
        {isSigner && !isAgent && (
          <Message variant="warning">
            <p>
              They are a signer on {signerOnSmartAccounts.length.toString()}{' '}
              {plural('account', signerOnSmartAccounts.length)}. You can proceed
              to remove them from the workspace, but they will remain a signer
              onchain. To remove them as a signer, you can go to the account
              settings later.
            </p>
          </Message>
        )}
      </div>
    </SplitsDialogContent>
  )
}
